import React, { useState, useMemo, useImperativeHandle, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import { checkCode } from './billingAccess'
import { subIsTeams, userHasCardOnFile, AllPayments } from './'
import './GiftCodeForm.less'

const GiftCodeForm = forwardRef(({ user, onCodeValidated, initialCode = '', disabled = false, onCancel }, ref) => {
  const [code, setCode] = useState(initialCode)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [teamsDowngradeError, setTeamsDowngradeError] = useState(false)
  const hasCard = !!userHasCardOnFile(user)

  // Track payment method status
  const userId = useMemo(() => path(['info', '_id'], user), [user])
  const [hasPaymentMethod, setHasPaymentMethod] = useState(hasCard)

  // Auto-validate when initialCode is provided
  useEffect(() => {
    if (initialCode && initialCode.trim() && !disabled) {
      handleSubmit()
    }
  }, [initialCode, disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    reset: () => {
      setCode('')
      setLoading(false)
      setError(false)
      setIsValid(false)
      setTeamsDowngradeError(false)
    }
  }))

  const handleSubmit = async (evt) => {
    if (evt) {
      evt.preventDefault()
    }

    if (!code || isValid) return

    setLoading(true)
    setError(false)
    setTeamsDowngradeError(false)
    
    try {
      const res = await checkCode(code)
      const valid = res && res.valid === true
      
      if (valid) {
        // Check if user has Teams subscription and code is for regular AWN+
        const isTeamsUser = user && user.plus && subIsTeams(user.plus)
        const isRegularAwnPlusCode = res.productName && /^awn-plus-account-/.test(res.productName)
        
        if (isTeamsUser && isRegularAwnPlusCode) {
          setTeamsDowngradeError(true)
          setError(false)
          setIsValid(false)
          return
        }
        
        setIsValid(true)
        setError(false)
        setTeamsDowngradeError(false)
        
        // Pass validated code info back to parent
        if (onCodeValidated) {
          onCodeValidated({
            code,
            productName: res.productName,
            valid: true
          })
        }
      } else {
        setError(true)
        setIsValid(false)
        setTeamsDowngradeError(false)
      }
    } catch (err) {
      setError(true)
      setIsValid(false)
      setTeamsDowngradeError(false)
    } finally {
      setLoading(false)
    }
  }

  const getButtonText = () => {
    if (isValid) return 'Applied'
    if (error || teamsDowngradeError) return 'Try again'
    return 'Apply code'
  }

  // Handle cancel button click
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  // Handle input change
  const handleInputChange = (evt) => {
    const newCode = evt.target.value.toUpperCase()
    setCode(newCode)
  }

  return (
    <div className='payment-gift-code-form'>
      {!hasPaymentMethod && (
        <>
          <div className='card-required-box'>
            <p><strong>To redeem gift code, please register your card first.</strong></p>
            <p>Your card is required to verify your account and ensure continued access to your premium features after your free year. You won't be charged today, and you can turn off auto-renewal at any time.</p>
          </div>
          <AllPayments
            userId={userId}
            mode='file'
            onSuccess={() => setHasPaymentMethod(true)}
          />
        </>
      )}
      {hasPaymentMethod && (
        <form onSubmit={handleSubmit}>
          <div className='input-group'>
            <input
              type='text'
              placeholder='Enter gift code'
              disabled={disabled || isValid || loading}
              className={error ? 'error' : ''}
              value={code}
              onChange={handleInputChange}
            />
          </div>
          {error && <p className='error'>Invalid code</p>}
          {teamsDowngradeError && (
            <p className='error'>
              This gift code is for AWN+ which cannot be applied to your AWN+ Teams subscription.
              Please wait until your Teams subscription expires before using this gift code.
            </p>
          )}
          <ul className='info-bullets'>
            <li>Free AWN+ plan or discount applied automatically based on the code type</li>
            <li>Any remaining subscription time will be transferred to your new plan</li>
            <li>Subscription renews automatically unless canceled</li>
            <li>Standard annual fee applies at renewal</li>
          </ul>
          <div className='button-group'>
            <button
              type='button'
              className='btn btn-secondary btn-cancel'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={disabled || isValid || loading || !code || teamsDowngradeError}
              className='btn btn-primary btn-apply'
            >
              {getButtonText()}
            </button>
          </div>
        </form>
      )}
    </div>
  )
})

GiftCodeForm.propTypes = {
  user: PropTypes.object.isRequired,
  onCodeValidated: PropTypes.func.isRequired,
  initialCode: PropTypes.string,
  disabled: PropTypes.bool,
  onCancel: PropTypes.func
}

GiftCodeForm.defaultProps = {
  initialCode: '',
  disabled: false
}

GiftCodeForm.displayName = 'GiftCodeForm'

export default GiftCodeForm